import type { SelectOption } from '@shared/components/Select.js';
import type { BasicReview } from '@shared/domain/review/index.js';
import type { UserType } from '@shared/domain/user/index.js';

export enum AuthEnum {
  COMPLETE_REGISTRATION = 'complete-registration',
  SIGN_IN = 'sign-in',
  SIGN_UP = 'sign-up',
  VERIFICATION = 'verification',
}

export enum SubscriptionPlan {
  FREE = 'free',
  INDIVIDUAL = 'individual',
  TEAM = 'team',
}

export enum SubscriptionStatus {
  ACTIVE = 'active',
  CANCELLED = 'cancelled',
  EXPIRED = 'expired',
  TRIAL_EXPIRED = 'trial_expired',
}

export enum SubscriptionTerm {
  CUSTOM = 'custom',
  FOREVER = 'forever',
  MONTHLY = 'monthly',
  YEARLY = 'yearly',
}

export enum UserPlanSlug {
  CONTRIBUTOR = 'contributor',
  FREE = 'free',
  INDIVIDUAL = 'individual',
  TEAM_BASIC = 'team_basic',
  TEAM_STANDARD = 'team_standard',
  ENTERPRISE = 'enterprise',
  // Retired tier kept for historical records
  KANDIR_PRO = 'kandir_pro',
}

export type AccountProperties = {
  address?: AddressProperties;
  addressId: number;
  billingEmail?: string;
  billingId?: string;
  billingPhone?: string;
  companySize: string;
  readonly id: number;
  industry?: string;
  name: string;
  ownerId?: number;
  shipToAddress?: AddressProperties;
  shipToAddressId?: number;
  subscriptions?: SubscriptionProperties[];
  readonly uuid: string;
} & AuditAttributes;

export type AccountUserProperties = {
  account?: AccountProperties;
  accountId: number;
  id: number;
  role?: RoleProperties;
  roleId: number;
  userId: number;
} & AuditAttributes;

export type AddressProperties = {
  addressLine1: string;
  addressLine2?: string;
  city: string;
  country: string;
  id: number;
  postalCode: string;
  stateProvince: string;
} & AuditAttributes;

export interface AuditAttributes {
  createdAt?: Date;
  updatedAt?: Date;
}

export interface AuthFormFieldsValues {
  address?: string;
  company: string;
  companyName?: string;
  companySize?: string;
  email: string;
  firstName: string;
  imageRef?: string;
  industry: string;
  lastName: string;
  password?: string;
  positionTitle: string;
  profile?: string;
  revenue: string;
  url?: string;
  verificationCode?: string;
  yearFounded?: string;
}

export type Company = {
  averageRating?: string;
  cik?: null | string;
  city?: string;
  clarity?: string;
  companyName?: string;
  companySize?: string;
  country?: string;
  createdBy?: number;
  email?: string;
  followerCount?: number;
  gicsCode?: null | string;
  gicsIndustry?: string;
  gicsIndustryGroup?: string;
  gicsSector?: string;
  gicsSubIndustry?: string;
  hasCompletedRegistration?: boolean;
  fiscalYearEndMonth?: number | null;
  headcount?: number;
  id?: number;
  idToken?: string;
  imageRef?: string;
  isApproved?: boolean;
  isPaidMember?: boolean;
  profile?: string;
  responsiveness?: string;
  revenue?: string;
  reviewCount?: number;
  reviews?: Array<BasicReview>;
  state?: string;
  stockTicker?: null | string;
  tags?: Array<CompanyTag>;
  transparency?: string;
  understanding?: string;
  url?: null | string;
  userType?: UserType;
  yearFounded?: string;
} & AuditAttributes;

export type CompanyTag = {
  id: number;
  name: string;
} & AuditAttributes;

export interface FormField {
  label: string;
  name: string;
  options?: Array<SelectOption>;
  sm?: number;
  testId?: string;
  type?: string;
  xs?: number;
}

export type FormFieldsValues
  = | AuthFormFieldsValues
    | CompleteRegistrationFormFieldsValues;

export interface IdTokenData {
  'cognito:username': string;
  'email': string;
}

export interface OnboardingConfigItem {
  completed?: boolean;
  hidden?: boolean;
  id: number;
  name?: string;
  text?: string;
}

export type PlanProperties = {
  features: Record<string, unknown>;
  name: string;
  slug: string;
} & AuditAttributes;

export type RoleProperties = {
  id: number;
  name: string;
} & AuditAttributes;

export type Subscription = {
  endDate?: Date;
  id?: number;
  plan: SubscriptionPlan;
  startDate?: Date;
  status: SubscriptionStatus;
  term: SubscriptionTerm;
  userId: number;
} & AuditAttributes;

export interface SubscriptionPeriodProperties {
  endDate?: Date;
  startDate: Date;
  term: SubscriptionTerm;
}

export type SubscriptionProperties = {
  accountId: number;
  id: number;
  period: SubscriptionPeriodProperties;
  plan?: PlanProperties;
  planSlug?: string;
  status: SubscriptionStatus;
} & AuditAttributes;

export type User = {
  accessToken?: string;
  accountMemberships?: Array<AccountUserProperties>;
  appOnboardingComplete?: boolean;
  avatarColor?: string;
  campaign?: string;
  Companies?: Array<Company>;
  company?: string;
  companySize?: string;
  deepDivesAvailableInSubPeriod?: number | null;
  deepDivesSubmittedInSubPeriod?: number;
  deepDivesViewedInSubPeriod?: number;
  email: string;
  favoriteReviews?: Array<{ reviewId: number }>;
  firstName?: string;
  followedCompanies?: Array<Company>;
  hasCompletedRegistration?: boolean;
  hourlyRate?: number;
  id?: number;
  idToken?: string;
  industry?: string;
  intelligenceRequestsAvailableInSubPeriod?: number;
  isAdmin: boolean;
  isPaidMember?: boolean;
  landingPage?: string;
  lastName?: string;
  loginMethod?: 'cognito' | 'sso';
  maxConsumableDeepDivesInSubPeriod?: number | null;
  networkOnboardingComplete?: boolean;
  onboardingConfig?: Array<OnboardingConfigItem>;
  planDeepDiveReviewLimit?: number | null;
  planIntelligenceRequestsLimit?: number;
  planSlug?: null | string;
  role?: string;
  subscriptionStatus?: string;
  positionTitle?: string;
  referer?: string;
  reviews?: Array<BasicReview>;
  source?: string;
  subscription?: Subscription;
  tagLine?: string;
  username?: string;
  userPreference?: UserPreferenceProperties;
  userType?: string;
} & AuditAttributes;

export type UserPreferenceProperties = {
  id: number;
  showAIValidation: boolean;
  showDealList: boolean;
  useCases?: string[];
  userId: number;
} & AuditAttributes;

export interface UserRefererInfo {
  numberOfReferrals: number;
  numberOfReviews: number;
}

interface CompleteRegistrationFormFieldsValues {
  firstName: string;
  industry?: string;
  lastName: string;
}

export const UserPoolData = {
  ClientId: import.meta.env.VITE_COGNITO_CLIENT_ID as string,
  UserPoolId: import.meta.env.VITE_COGNITO_USER_POOL_ID as string,
};

export const SIGN_UP_FORM_FIELDS: Array<FormField> = [
  {
    label: 'First Name',
    name: 'firstName',
    testId: 'sign-up-first-name',
    type: 'text',
  },
  {
    label: 'Last Name',
    name: 'lastName',
    testId: 'sign-up-last-name',
    type: 'text',
  },
  {
    label: 'Email',
    name: 'email',
    testId: 'sign-up-email',
    type: 'text',
  },
  {
    label: 'Current Company',
    name: 'company',
    testId: 'sign-up-company',
    type: 'text',
  },
  // {
  //   name: 'positionTitle',
  //   label: 'Position Title',
  //   type: 'text',
  //   testId: 'sign-up-positionTitle',
  // },
  // {
  //   name: 'companySize',
  //   label: 'Company Size',
  //   type: 'select',
  //   options: COMPANY_SIZE_OPTIONS,
  //   testId: 'sign-up-companySize',
  // },
  {
    label: 'Password',
    name: 'password',
    testId: 'sign-up-password',
    type: 'password',
  },
];

export const VERIFICATION_FORM_FIELDS: Array<FormField> = [
  {
    label: 'Verification Code',
    name: 'verificationCode',
    type: 'text',
    xs: 12,
  },
];

export const SIGN_IN_FORM_FIELDS: Array<FormField> = [
  {
    label: 'Email',
    name: 'email',
    testId: 'sign-in-email',
    type: 'email',
    xs: 12,
  },
  {
    label: 'Password',
    name: 'password',
    testId: 'sign-in-password',
    type: 'password',
    xs: 12,
  },
];
